import { useEffect, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Caption1, makeStyles } from '@fluentui/react-components';
import { personDisplayName, type Person } from '@vigeo/shared';
import { SidePanel, StatusBadge, StatusDot, complianceTokenFor, tokens } from '@vigeo/ui';
import { lifecyclePresenters } from '@/features/candidates';
import { complianceAndDocumentsShown } from '@/lib/sections';
import { formatPhone } from '@/features/roster';
import { ComplianceMark, useCompliance, useRoleOptions, useWorkflow } from './data.js';
import { FactRows, Initials, ServiceAreaChips, useProfileStyles } from './parts.js';
import { dayOf, employmentTypeLine, expiringCount, hireDay, nextToExpire, notesOf, roleLabels } from './profile.js';

const useStyles = makeStyles({
  who: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, minWidth: 0 },
  whoMain: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  name: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  meta: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  line: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', paddingBottom: tokens.spacingVerticalM },
  part: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, paddingBlock: tokens.spacingVerticalM,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  partTitle: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
});

/** The address of a person's full profile. The id is opaque; nothing about the person is in it. */
export const profilePath = (personId: string): string => `/app/roster/${personId}`;

export interface QuickLookPanelProps {
  /** The person to show, or null when the panel is closed. */
  readonly person: Person | null;
  readonly onClose: () => void;
  /** The name that opened it; the keyboard goes back there. */
  readonly opener?: RefObject<HTMLElement | null>;
}

/**
 * A quick look at one staff member, over the Roster (#585, Figma `1097:1222`): status, role, compliance, contact,
 * employment, service areas, what comes due next and the latest note. Nothing is edited here, and it holds no files.
 */
export function QuickLookPanel({ person, onClose, opener }: QuickLookPanelProps) {
  const navigate = useNavigate();
  /**
   * "Open full profile" CLOSES the panel first, and goes one tick after it has closed.
   *
   * Going while it was open, or in the same tick it closed, left the whole page hidden from a screen reader for good: the app's
   * root kept `aria-hidden="true"`. Fluent's modal hands the page back when the keyboard leaves it, and the panel gives the keyboard
   * back to the name one tick after closing (`SidePanel`); leaving sooner takes that name off the page before it can be focused.
   * This effect runs after the panel's own, so its timer fires after the panel's. Pinned in `e2e/employee-profile.spec.ts`, which
   * finds the profile's heading by role and cannot when the page is hidden.
   */
  const [goingTo, setGoingTo] = useState<string | null>(null);
  useEffect(() => {
    if (person !== null || goingTo === null) return;
    const timer = setTimeout(() => { void navigate(goingTo); }, 0);
    return () => clearTimeout(timer);
  }, [person, goingTo, navigate]);
  return (
    <SidePanel
      open={person !== null}
      ariaLabel={person === null ? 'Staff member' : `Quick look: ${personDisplayName(person.name)}`}
      onClose={onClose}
      opener={opener}
      header={person === null ? null : <Who person={person} />}
      footer={person === null ? undefined : (
        <>
          <Button appearance="secondary" onClick={onClose}>Close</Button>
          <Button appearance="primary" onClick={() => { setGoingTo(profilePath(person.id)); onClose(); }}>Open full profile</Button>
        </>
      )}
    >
      {person === null ? null : <Body person={person} />}
    </SidePanel>
  );
}

function Who({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const shared = useProfileStyles();
  const options = useRoleOptions();
  const roles = roleLabels(person, options.data?.jobProfiles);
  const display = personDisplayName(person.name);
  return (
    <div className={styles.who}>
      <Initials person={person} small />
      <div className={styles.whoMain}>
        <h2 className={styles.name}>{display}</h2>
        <div className={styles.meta}>
          <StatusDot color={person.lifecycle === 'active' ? complianceTokenFor.good : tokens.colorNeutralForeground3}>{lifecyclePresenters[person.lifecycle].label}</StatusDot>
          <span aria-hidden="true" className={shared.quiet}>·</span>
          <span>{roles.length === 0 ? 'No role set' : roles.join(' · ')}</span>
        </div>
      </div>
    </div>
  );
}

function Body({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const withTracking = complianceAndDocumentsShown();
  const compliance = useCompliance(person, withTracking);
  const options = useRoleOptions();
  const workflow = useWorkflow(person.id);
  const roles = roleLabels(person, options.data?.jobProfiles);
  const emergency = person.contact.emergencyContacts?.[0];
  const dated = compliance.status === 'ready' ? nextToExpire(compliance.checklist, 3) : [];
  const expiring = compliance.status === 'ready' ? expiringCount(compliance.checklist) : 0;
  const note = workflow.data === undefined ? undefined : notesOf(workflow.data)[0];
  return (
    <>
      {withTracking ? (
        <div className={styles.line}>
          <Caption1>Compliance</Caption1>
          <ComplianceMark compliance={compliance} />
          {expiring > 0 ? <StatusBadge tone="warning">{expiring} expiring or expired</StatusBadge> : null}
        </div>
      ) : null}
      <section className={styles.part} aria-label="Contact">
        <h3 className={styles.partTitle}>Contact</h3>
        <FactRows facts={[
          { label: 'Cell phone', value: formatPhone(person.contact.phone) },
          { label: 'Email', value: person.contact.email },
          { label: 'Emergency', value: emergency === undefined ? null : `${emergency.name} (${emergency.relationship}) · ${formatPhone(emergency.phone)}` },
        ]} />
      </section>
      <section className={styles.part} aria-label="Employment">
        <h3 className={styles.partTitle}>Employment</h3>
        <FactRows facts={[
          { label: 'Role', value: roles.length === 0 ? <span>No role set</span> : roles.join(' · ') },
          { label: 'Type', value: employmentTypeLine(person) },
          { label: 'Hired', value: hireDay(person) },
          { label: 'Supervisor', value: person.assignment?.supervisorName },
        ]} />
      </section>
      <section className={styles.part} aria-label="Where">
        <h3 className={styles.partTitle}>Where</h3>
        <FactRows facts={[{ label: 'Service areas', value: <ServiceAreaChips person={person} /> }]} />
      </section>
      {withTracking ? (
        <section className={styles.part} aria-label="Compliance, next to expire">
          <h3 className={styles.partTitle}>Compliance, next to expire</h3>
          {compliance.status === 'untracked' ? <Caption1>Not tracked yet. Nothing here counts for or against the agency.</Caption1>
            : compliance.status === 'pending' ? <Caption1>Working it out…</Caption1>
              : compliance.status === 'failed' ? <Caption1>The checklist could not be read just now.</Caption1>
                : dated.length === 0 ? <Caption1>Nothing with a date is coming due.</Caption1>
                  : <FactRows wide facts={dated.map((item) => ({ label: item.label, value: dayOf(item.dueAt!) }))} />}
        </section>
      ) : null}
      <section className={styles.part} aria-label="Latest note">
        <h3 className={styles.partTitle}>Latest note</h3>
        {workflow.isPending ? <Caption1>Loading…</Caption1> : workflow.isError ? <Caption1>Unavailable</Caption1>
          : note === undefined ? <Caption1>No notes yet</Caption1>
            : <FactRows facts={[{ label: dayOf(note.at), value: `“${note.text}”` }]} />}
      </section>
    </>
  );
}
